import React, { useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function ThemeBlocks({ onReset, onBack }) {
  const { appState } = useContext(AppContext);
  const grupos = appState.grupos || [];

  const [yearStart, setYearStart] = useState(2023);
  const [yearEnd, setYearEnd]     = useState(new Date().getFullYear());

  const [blocks, setBlocks] = useState(
    grupos.map(g => ({
      tema: g.tema,
      descricao: g.descricao || '',
      icone: g.icone || '📚',
      topicos: g.topicos || [],
      searchStatus: 'idle',
      articles: [],
      generateStatus: 'idle',
      sessionId: null,
      filename: null,
      error: null
    }))
  );

  const updateBlock = (idx, updates) =>
    setBlocks(prev => prev.map((b, i) => i === idx ? { ...b, ...updates } : b));

  // Reseta buscas de todos os blocos (mantém apostilas já prontas)
  const resetSearches = () => {
    setBlocks(prev => prev.map(b => ({
      ...b,
      searchStatus: 'idle',
      articles: [],
      error: b.generateStatus === 'done' ? null : null
    })));
  };

  const searchBlock = async (idx) => {
    updateBlock(idx, { searchStatus: 'searching', error: null, articles: [] });
    try {
      const res = await fetch(`${API}/materials/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topicos: blocks[idx].topicos, yearStart, yearEnd })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro na busca');
      updateBlock(idx, { searchStatus: 'done', articles: data.articles || [] });
    } catch (e) {
      updateBlock(idx, { searchStatus: 'error', error: e.message });
    }
  };

  const searchAll = () =>
    blocks.forEach((b, idx) => { if (b.searchStatus === 'idle') searchBlock(idx); });

  const generateApostila = async (idx) => {
    const block = blocks[idx];
    updateBlock(idx, { generateStatus: 'generating', error: null });
    try {
      const res = await fetch(`${API}/materials/generate-apostila`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tema: block.tema,
          topicos: block.topicos,
          articles: block.articles,
          edition: new Date().getFullYear(),
          yearStart,
          yearEnd
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao gerar apostila');
      updateBlock(idx, { generateStatus: 'done', sessionId: data.id, filename: data.filename });
    } catch (e) {
      updateBlock(idx, { generateStatus: 'error', error: e.message });
    }
  };

  const downloadApostila = (block) => {
    const a = document.createElement('a');
    a.href = `${API}/materials/download/${block.sessionId}/apostila`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const anySearching  = blocks.some(b => b.searchStatus === 'searching');
  const pendingSearch = blocks.filter(b => b.searchStatus === 'idle').length;
  const anySearchDone = blocks.some(b => b.searchStatus === 'done' || b.searchStatus === 'error');

  return (
    <div className="step-content">

      {/* Painel de controle da busca */}
      <div className="card" style={{ marginBottom: 24, background: 'linear-gradient(135deg, #eaf2fb, #f5f9ff)', border: '2px solid #385e81' }}>
        <h4 style={{ color: '#385e81', marginBottom: 12 }}>📅 Período de Busca Científica</h4>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#555' }}>De</span>
            <input
              type="number" value={yearStart}
              onChange={e => setYearStart(Number(e.target.value))}
              className="input" style={{ width: 90, textAlign: 'center', fontWeight: 700 }}
            />
            <span style={{ fontSize: 13, color: '#555' }}>até</span>
            <input
              type="number" value={yearEnd}
              onChange={e => setYearEnd(Number(e.target.value))}
              className="input" style={{ width: 90, textAlign: 'center', fontWeight: 700 }}
            />
          </div>

          {pendingSearch > 0 && !anySearching && (
            <button onClick={searchAll} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
              🔍 Buscar em todos os {blocks.length} temas
            </button>
          )}

          {anySearchDone && !anySearching && (
            <button
              onClick={resetSearches}
              className="btn btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
              title="Altere as datas acima e clique aqui para refazer todas as buscas"
            >
              🔄 Refazer buscas com novas datas
            </button>
          )}
        </div>

        <p style={{ fontSize: 12, color: '#666', marginTop: 10, marginBottom: 0 }}>
          A busca é feita em 5 fontes científicas por tema: PubMed, Europe PMC, Semantic Scholar, OpenAlex e CrossRef.
          {anySearchDone && ' Para mudar o período, altere as datas e clique em "Refazer buscas".'}
        </p>
      </div>

      {/* Blocos por tema */}
      {blocks.map((block, idx) => (
        <div key={idx} className="card" style={{
          marginBottom: 20,
          borderLeft: `5px solid ${block.generateStatus === 'done' ? '#2e7d32' : '#385e81'}`,
          transition: 'border-color 0.3s'
        }}>

          {/* Cabeçalho */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
            <span style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{block.icone}</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, color: '#385e81', fontSize: '1.1rem' }}>{block.tema}</h3>
              {block.descricao && (
                <p style={{ margin: '4px 0 6px', color: '#666', fontSize: 13 }}>{block.descricao}</p>
              )}
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#888', fontWeight: 600 }}>
                  {block.topicos.length} tópicos para busca
                </summary>
                <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                  {block.topicos.map((t, i) => (
                    <li key={i} style={{ fontSize: 11, color: '#555', marginBottom: 3 }}>🔬 {t}</li>
                  ))}
                </ul>
              </details>
            </div>
          </div>

          {/* Área de busca */}
          {block.searchStatus === 'idle' && (
            <button onClick={() => searchBlock(idx)} className="btn btn-secondary" style={{ width: '100%', marginBottom: 12 }}>
              🔍 Buscar atualizações ({yearStart}–{yearEnd}) sobre {block.tema}
            </button>
          )}

          {block.searchStatus === 'searching' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#eaf2fb', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
              <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2, flexShrink: 0 }}></div>
              <span style={{ fontSize: 13, color: '#385e81' }}>
                Buscando em PubMed, Europe PMC, Semantic Scholar, OpenAlex e CrossRef...
              </span>
            </div>
          )}

          {block.searchStatus === 'done' && (
            <div style={{ background: '#f0f7f0', border: '1px solid #c8e6c9', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: block.articles.length > 0 ? 8 : 0 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#2e7d32' }}>
                  ✅ {block.articles.length} artigos encontrados ({yearStart}–{yearEnd})
                </span>
                <button
                  onClick={() => { updateBlock(idx, { searchStatus: 'idle', articles: [], generateStatus: block.generateStatus === 'done' ? 'done' : 'idle' }); }}
                  style={{ background: 'none', border: '1px solid #999', borderRadius: 4, fontSize: 11, color: '#666', cursor: 'pointer', padding: '2px 8px' }}
                >
                  Refazer
                </button>
              </div>
              {block.articles.slice(0, 4).map((a, i) => (
                <div key={i} style={{ fontSize: 12, color: '#444', borderTop: '1px solid #c8e6c9', paddingTop: 6, marginTop: 6 }}>
                  <strong>{i + 1}.</strong> {a.title}
                  {a.date && <span style={{ color: '#777' }}> ({a.date})</span>}
                  {a.source && <span style={{ marginLeft: 6, fontSize: 10, background: '#d4edda', color: '#155724', padding: '1px 5px', borderRadius: 3 }}>{a.source}</span>}
                </div>
              ))}
              {block.articles.length > 4 && (
                <div style={{ fontSize: 11, color: '#777', marginTop: 6 }}>
                  + {block.articles.length - 4} artigo(s) adicional(is) incluídos na apostila
                </div>
              )}
            </div>
          )}

          {block.searchStatus === 'error' && (
            <div style={{ color: '#c62828', fontSize: 13, marginBottom: 12, padding: '10px 14px', background: '#fff3f3', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>❌ {block.error}</span>
              <button onClick={() => searchBlock(idx)} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #c62828', borderRadius: 4, color: '#c62828', cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}>
                Tentar novamente
              </button>
            </div>
          )}

          {/* Aviso: sem artigos, não dá pra gerar com segurança (evita alucinação de referências) */}
          {block.searchStatus === 'done' && block.articles.length === 0 && (
            <div style={{ color: '#b26a00', fontSize: 13, marginBottom: 12, padding: '10px 14px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8 }}>
              ⚠️ Nenhum artigo encontrado para <strong>{block.tema}</strong> em {yearStart}–{yearEnd}.
              Sem fontes reais a apostila não pode ser criada com citações confiáveis — amplie o período de busca acima (ex: 2018–{new Date().getFullYear()}) e clique em "Refazer buscas".
            </div>
          )}

          {/* Botão criar apostila — só aparece se há pelo menos 1 artigo real */}
          {block.searchStatus === 'done' && block.articles.length > 0 && block.generateStatus === 'idle' && (
            <button onClick={() => generateApostila(idx)} className="btn btn-primary" style={{ width: '100%', padding: '14px 20px', fontSize: 15 }}>
              📖 Criar Apostila — {block.tema}
            </button>
          )}

          {block.generateStatus === 'generating' && (
            <div style={{ textAlign: 'center', padding: '28px 0', borderTop: '1px solid #eee', marginTop: 4 }}>
              <div className="spinner" style={{ margin: '0 auto 14px' }}></div>
              <p style={{ color: '#385e81', fontWeight: 600, marginBottom: 4 }}>Gerando apostila de <strong>{block.tema}</strong>...</p>
              <p style={{ color: '#888', fontSize: 12, margin: 0 }}>Criando conteúdo, imagens e documento Word. Pode levar 2–3 minutos.</p>
            </div>
          )}

          {block.generateStatus === 'done' && (
            <div>
              <button onClick={() => downloadApostila(block)} style={{
                width: '100%', padding: '15px 20px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10
              }}>
                ⬇️ Baixar Apostila
                <span style={{ fontSize: 12, opacity: 0.85, fontWeight: 400 }}>{block.filename}</span>
              </button>
              <button
                onClick={() => { updateBlock(idx, { generateStatus: 'idle', sessionId: null, filename: null }); }}
                style={{ width: '100%', marginTop: 8, background: 'none', border: '1px solid #aaa', borderRadius: 6, padding: '6px 0', fontSize: 12, color: '#666', cursor: 'pointer' }}
              >
                🔁 Regenerar apostila
              </button>
            </div>
          )}

          {block.generateStatus === 'error' && (
            <div>
              <div style={{ color: '#c62828', fontSize: 13, marginBottom: 10, padding: '10px 14px', background: '#fff3f3', borderRadius: 8 }}>
                ❌ {block.error}
              </div>
              <button onClick={() => generateApostila(idx)} className="btn btn-secondary">Tentar novamente</button>
            </div>
          )}
        </div>
      ))}

      {/* Rodapé */}
      <div style={{ display: 'flex', gap: 12, marginTop: 8, paddingTop: 20, borderTop: '1px solid #eee' }}>
        <button onClick={onBack} className="btn btn-secondary">
          ← Voltar aos temas
        </button>
        <button onClick={onReset} className="btn btn-secondary">
          🔄 Processar novo material
        </button>
      </div>
    </div>
  );
}
